import React, { useState, useEffect, useCallback } from 'react';
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  Clock,
  Plus,
  Search,
  Filter,
  Calendar,
  Edit2,
  Trash2,
  DollarSign,
  AlertCircle,
  CheckCircle2,
  X,
  RefreshCw,
  PieChart,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { expenseService } from '../../services/expenseService';

const CATEGORIES = [
  'Sales',
  'Services',
  'Rent',
  'Salaries',
  'Utilities',
  'Inventory',
  'Maintenance',
  'Marketing',
  'Logistics',
  'Other'
];

const PAYMENT_MODES = [
  { id: 'cash', label: 'Cash' },
  { id: 'upi', label: 'UPI / QR' },
  { id: 'card', label: 'Card' },
  { id: 'bank_transfer', label: 'Bank Transfer' },
  { id: 'cheque', label: 'Cheque' },
  { id: 'other', label: 'Other' }
];

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState({
    totalCredit: 0,
    totalDebit: 0,
    paidCredit: 0,
    paidDebit: 0,
    pendingCredit: 0,
    pendingDebit: 0,
    netProfit: 0,
    profitMargin: 0,
    isProfit: true,
    count: 0
  });
  const [loading, setLoading] = useState(true);

  // Filters
  const [timeframe, setTimeframe] = useState('monthly'); // 'daily' | 'weekly' | 'monthly' | 'all' | 'custom'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'credit' | 'debit'
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'paid' | 'partially_paid' | 'pending'
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    type: 'debit',
    amount: '',
    paidAmount: '',
    paymentMode: 'cash',
    category: 'Other',
    date: new Date().toISOString().split('T')[0],
    remarks: ''
  });

  // Fetch Expenses Function
  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        timeframe,
        type: typeFilter,
        paymentStatus: statusFilter,
        category: categoryFilter,
        search: searchQuery
      };

      if (timeframe === 'custom') {
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
      }

      const res = await expenseService.getExpenses(params);
      if (res.success) {
        setExpenses(res.data || []);
        if (res.summary) setSummary(res.summary);
      }
    } catch (err) {
      console.error('Fetch expenses error:', err);
      toast.error(err?.response?.data?.message || 'Failed to load expense records');
    } finally {
      setLoading(false);
    }
  }, [timeframe, startDate, endDate, typeFilter, statusFilter, categoryFilter, searchQuery]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  // Open Create Modal
  const handleOpenCreateModal = () => {
    setEditingExpense(null);
    setFormData({
      title: '',
      type: 'debit',
      amount: '',
      paidAmount: '',
      paymentMode: 'cash',
      category: 'Other',
      date: new Date().toISOString().split('T')[0],
      remarks: ''
    });
    setIsModalOpen(true);
  };

  // Open Edit Modal
  const handleOpenEditModal = (item) => {
    setEditingExpense(item);
    setFormData({
      title: item.title || '',
      type: item.type || 'debit',
      amount: item.amount || '',
      paidAmount: item.paidAmount !== undefined ? item.paidAmount : '',
      paymentMode: item.paymentMode || 'cash',
      category: item.category || 'Other',
      date: item.date ? new Date(item.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      remarks: item.remarks || ''
    });
    setIsModalOpen(true);
  };

  // Handle Submit Form
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.amount) {
      toast.error('Please fill in title and amount');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        amount: Number(formData.amount),
        paidAmount: formData.paidAmount !== '' ? Number(formData.paidAmount) : 0
      };

      if (editingExpense) {
        const res = await expenseService.updateExpense(editingExpense._id, payload);
        if (res.success) {
          toast.success('Transaction updated successfully');
          setIsModalOpen(false);
          fetchExpenses();
        }
      } else {
        const res = await expenseService.createExpense(payload);
        if (res.success) {
          toast.success('Transaction created successfully');
          setIsModalOpen(false);
          fetchExpenses();
        }
      }
    } catch (err) {
      console.error('Save expense error:', err);
      toast.error(err?.response?.data?.message || 'Failed to save transaction');
    } finally {
      setSubmitting(false);
    }
  };

  // Delete Expense
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this transaction record?')) return;

    try {
      const res = await expenseService.deleteExpense(id);
      if (res.success) {
        toast.success('Transaction deleted');
        fetchExpenses();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to delete transaction');
    }
  };

  // Format Currency
  const formatINR = (val) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(val || 0);
  };

  return (
    <div className="space-y-6 antialiased">
      {/* 1. Header & Quick Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200/80 shadow-xs">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-indigo-50 text-indigo-600">
              <Wallet size={24} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 tracking-tight">Expense & P&L Tracker</h1>
              <p className="text-xs text-slate-500 font-medium">
                Monitor income, expenses, profit/loss, and outstanding balance status
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={handleOpenCreateModal}
          className="inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-4 py-2.5 rounded-xl shadow-sm transition-all duration-150 shrink-0"
        >
          <Plus size={16} />
          <span>Add Transaction</span>
        </button>
      </div>

      {/* 2. Timeframe Quick Selector Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-3.5 rounded-xl border border-slate-200/80 shadow-xs">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-bold text-slate-400 uppercase tracking-wider px-2">Timeframe:</span>
          {[
            { id: 'daily', label: 'Today (Daily)' },
            { id: 'weekly', label: 'This Week' },
            { id: 'monthly', label: 'This Month' },
            { id: 'all', label: 'All Time' },
            { id: 'custom', label: 'Custom Range' },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTimeframe(t.id)}
              className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-all ${
                timeframe === t.id
                  ? 'bg-slate-900 text-white shadow-xs'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {timeframe === 'custom' && (
          <div className="flex items-center gap-2 text-xs">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-2.5 py-1.5 border border-slate-200 rounded-lg text-slate-700 outline-none focus:border-indigo-500"
            />
            <span className="text-slate-400">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-2.5 py-1.5 border border-slate-200 rounded-lg text-slate-700 outline-none focus:border-indigo-500"
            />
          </div>
        )}
      </div>

      {/* 3. Profit & Loss (P&L) Analytical Banner & Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        
        {/* P&L Main Card */}
        <div
          className={`p-5 rounded-2xl border shadow-xs relative overflow-hidden transition-all ${
            summary.isProfit
              ? 'bg-gradient-to-br from-emerald-500 to-teal-700 text-white border-emerald-600'
              : 'bg-gradient-to-br from-rose-500 to-red-700 text-white border-rose-600'
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider opacity-85">
              Net Profit / Loss
            </span>
            <div className="p-1.5 rounded-lg bg-white/20 backdrop-blur-xs">
              {summary.isProfit ? <ArrowUpRight size={18} /> : <ArrowDownRight size={18} />}
            </div>
          </div>
          <div className="text-2xl font-bold tracking-tight">
            {formatINR(summary.netProfit)}
          </div>
          <div className="mt-2 flex items-center justify-between text-xs opacity-90">
            <span>Profit Margin:</span>
            <span className="font-semibold px-2 py-0.5 rounded-md bg-white/20 backdrop-blur-xs">
              {summary.profitMargin}%
            </span>
          </div>
        </div>

        {/* Total Credit / Revenue Card */}
        <div className="p-5 rounded-2xl bg-white border border-slate-200/80 shadow-xs space-y-2">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-xs font-bold uppercase tracking-wider">Total Credit (Income)</span>
            <div className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600">
              <TrendingUp size={18} />
            </div>
          </div>
          <div className="text-2xl font-bold text-slate-900">{formatINR(summary.totalCredit)}</div>
          <div className="flex items-center justify-between text-xs text-slate-500 pt-1 border-t border-slate-100">
            <span>Received: <strong className="text-emerald-600">{formatINR(summary.paidCredit)}</strong></span>
            <span>Pending: <strong className="text-amber-600">{formatINR(summary.pendingCredit)}</strong></span>
          </div>
        </div>

        {/* Total Debit / Expense Card */}
        <div className="p-5 rounded-2xl bg-white border border-slate-200/80 shadow-xs space-y-2">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-xs font-bold uppercase tracking-wider">Total Debit (Expenses)</span>
            <div className="p-1.5 rounded-lg bg-rose-50 text-rose-600">
              <TrendingDown size={18} />
            </div>
          </div>
          <div className="text-2xl font-bold text-slate-900">{formatINR(summary.totalDebit)}</div>
          <div className="flex items-center justify-between text-xs text-slate-500 pt-1 border-t border-slate-100">
            <span>Paid: <strong className="text-rose-600">{formatINR(summary.paidDebit)}</strong></span>
            <span>Owed: <strong className="text-amber-600">{formatINR(summary.pendingDebit)}</strong></span>
          </div>
        </div>

        {/* Outstanding Receivables & Payables Balance */}
        <div className="p-5 rounded-2xl bg-white border border-slate-200/80 shadow-xs space-y-2">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-xs font-bold uppercase tracking-wider">Pending Balances</span>
            <div className="p-1.5 rounded-lg bg-amber-50 text-amber-600">
              <Clock size={18} />
            </div>
          </div>
          <div className="text-2xl font-bold text-amber-600">
            {formatINR(summary.pendingCredit + summary.pendingDebit)}
          </div>
          <div className="flex items-center justify-between text-xs text-slate-500 pt-1 border-t border-slate-100">
            <span>To Collect: <strong>{formatINR(summary.pendingCredit)}</strong></span>
            <span>To Pay: <strong>{formatINR(summary.pendingDebit)}</strong></span>
          </div>
        </div>

      </div>

      {/* 4. Filter & Search Toolbar */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs space-y-3 md:space-y-0 md:flex md:items-center md:justify-between md:gap-4">
        
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by title, category, remarks..."
            className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-xl text-xs outline-none focus:border-indigo-500 bg-slate-50/50"
          />
        </div>

        {/* Filters Dropdown */}
        <div className="flex flex-wrap items-center gap-2.5">
          {/* Type Filter */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-xl text-xs text-slate-700 bg-white outline-none focus:border-indigo-500 font-medium"
          >
            <option value="all">All Types (Credit & Debit)</option>
            <option value="credit">Credit (Income)</option>
            <option value="debit">Debit (Expense)</option>
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-xl text-xs text-slate-700 bg-white outline-none focus:border-indigo-500 font-medium"
          >
            <option value="all">All Payment Statuses</option>
            <option value="paid">Paid</option>
            <option value="partially_paid">Partially Paid</option>
            <option value="pending">Pending (Unpaid)</option>
          </select>

          {/* Category Filter */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-xl text-xs text-slate-700 bg-white outline-none focus:border-indigo-500 font-medium"
          >
            <option value="all">All Categories</option>
            {CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          <button
            onClick={fetchExpenses}
            className="p-2 border border-slate-200 hover:bg-slate-50 rounded-xl text-slate-600 transition-colors"
            title="Refresh Data"
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* 5. Transactions Data Table */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50/80 text-slate-500 font-bold uppercase tracking-wider border-b border-slate-200/80">
              <tr>
                <th className="px-5 py-3.5">Date</th>
                <th className="px-5 py-3.5">Title & Category</th>
                <th className="px-5 py-3.5">Type</th>
                <th className="px-5 py-3.5">Total Amount</th>
                <th className="px-5 py-3.5">Paid Amount</th>
                <th className="px-5 py-3.5">Balance</th>
                <th className="px-5 py-3.5">Status</th>
                <th className="px-5 py-3.5">Mode</th>
                <th className="px-5 py-3.5">Remarks</th>
                <th className="px-5 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700 font-medium">
              {loading ? (
                <tr>
                  <td colSpan="10" className="px-5 py-12 text-center text-slate-400">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <RefreshCw size={24} className="animate-spin text-indigo-600" />
                      <span>Loading expense data...</span>
                    </div>
                  </td>
                </tr>
              ) : expenses.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-5 py-12 text-center text-slate-400">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <Wallet size={32} className="text-slate-300" />
                      <p className="font-semibold text-slate-600">No transaction records found</p>
                      <p className="text-xs text-slate-400">Try adjusting your filters or add a new transaction.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                expenses.map((item) => {
                  const balance = Math.max(0, (item.amount || 0) - (item.paidAmount || 0));

                  return (
                    <tr key={item._id} className="hover:bg-slate-50/60 transition-colors">
                      {/* Date */}
                      <td className="px-5 py-3.5 whitespace-nowrap text-slate-500">
                        {item.date ? new Date(item.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                      </td>

                      {/* Title & Category */}
                      <td className="px-5 py-3.5">
                        <div className="font-bold text-slate-900">{item.title}</div>
                        <span className="inline-block text-[10px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md mt-0.5">
                          {item.category || 'Other'}
                        </span>
                      </td>

                      {/* Type Badge (Credit / Debit) */}
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1 font-bold text-[11px] px-2.5 py-1 rounded-lg ${
                            item.type === 'credit'
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : 'bg-rose-50 text-rose-700 border border-rose-200'
                          }`}
                        >
                          {item.type === 'credit' ? (
                            <>
                              <ArrowUpRight size={14} /> Credit (Income)
                            </>
                          ) : (
                            <>
                              <ArrowDownRight size={14} /> Debit (Expense)
                            </>
                          )}
                        </span>
                      </td>

                      {/* Total Amount */}
                      <td className="px-5 py-3.5 whitespace-nowrap font-bold text-slate-900">
                        {formatINR(item.amount)}
                      </td>

                      {/* Paid Amount */}
                      <td className="px-5 py-3.5 whitespace-nowrap font-semibold text-emerald-600">
                        {formatINR(item.paidAmount)}
                      </td>

                      {/* Balance Amount */}
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        {balance > 0 ? (
                          <span className="font-bold text-amber-600">{formatINR(balance)}</span>
                        ) : (
                          <span className="text-slate-400">₹0</span>
                        )}
                      </td>

                      {/* Status Badge */}
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1 font-bold text-[10px] px-2 py-0.5 rounded-full ${
                            item.paymentStatus === 'paid'
                              ? 'bg-emerald-100 text-emerald-800'
                              : item.paymentStatus === 'partially_paid'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-rose-100 text-rose-800'
                          }`}
                        >
                          {item.paymentStatus === 'paid' && '✓ Paid'}
                          {item.paymentStatus === 'partially_paid' && '⏳ Partially Paid'}
                          {item.paymentStatus === 'pending' && '✕ Pending'}
                        </span>
                      </td>

                      {/* Payment Mode */}
                      <td className="px-5 py-3.5 whitespace-nowrap uppercase text-[11px] text-slate-500 font-semibold">
                        {item.paymentMode || 'Cash'}
                      </td>

                      {/* Remarks */}
                      <td className="px-5 py-3.5 max-w-[200px] truncate text-slate-500" title={item.remarks}>
                        {item.remarks || <span className="text-slate-300 italic">No remarks</span>}
                      </td>

                      {/* Actions */}
                      <td className="px-5 py-3.5 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => handleOpenEditModal(item)}
                            className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 text-slate-600 hover:text-indigo-600 transition-colors"
                            title="Edit Record"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(item._id)}
                            className="p-1.5 rounded-lg border border-slate-200 hover:bg-rose-50 text-slate-600 hover:text-rose-600 transition-colors"
                            title="Delete Record"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 6. Create / Edit Transaction Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 backdrop-blur-xs p-4 animate-fade-in">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl max-w-lg w-full overflow-hidden animate-scale-up">
            
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-base font-bold text-slate-900">
                {editingExpense ? 'Edit Transaction Record' : 'Add New Transaction'}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSubmitForm} className="p-6 space-y-4">
              
              {/* Type Switcher (Credit vs Debit) */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                  Transaction Type
                </label>
                <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-xl">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, type: 'credit' })}
                    className={`py-2 rounded-lg text-xs font-bold transition-all ${
                      formData.type === 'credit'
                        ? 'bg-emerald-600 text-white shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Credit (Income / Revenue)
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, type: 'debit' })}
                    className={`py-2 rounded-lg text-xs font-bold transition-all ${
                      formData.type === 'debit'
                        ? 'bg-rose-600 text-white shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Debit (Expense / Payout)
                  </button>
                </div>
              </div>

              {/* Title & Category */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Title / Description *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g. Office Rent, Service Payment"
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs outline-none focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs outline-none focus:border-indigo-500 bg-white"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Total Amount & Paid Amount */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Total Amount (₹) *
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    required
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="e.g. 5000"
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs outline-none focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Paid Amount (₹)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={formData.paidAmount}
                    onChange={(e) => setFormData({ ...formData, paidAmount: e.target.value })}
                    placeholder="Leave 0 if unpaid"
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs outline-none focus:border-indigo-500"
                  />
                </div>
              </div>

              {/* Live Remaining Balance Calculation Preview */}
              {formData.amount && (
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/80 flex items-center justify-between text-xs">
                  <span className="text-slate-500">Remaining Balance Owed:</span>
                  <span className="font-bold text-amber-600">
                    {formatINR(Math.max(0, Number(formData.amount || 0) - Number(formData.paidAmount || 0)))}
                  </span>
                </div>
              )}

              {/* Payment Mode & Date */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Payment Mode
                  </label>
                  <select
                    value={formData.paymentMode}
                    onChange={(e) => setFormData({ ...formData, paymentMode: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs outline-none focus:border-indigo-500 bg-white"
                  >
                    {PAYMENT_MODES.map((m) => (
                      <option key={m.id} value={m.id}>{m.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Transaction Date
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs outline-none focus:border-indigo-500"
                  />
                </div>
              </div>

              {/* Remarks */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Remarks / Notes
                </label>
                <textarea
                  rows="2"
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  placeholder="Add optional notes, invoice reference, or vendor/customer remarks..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs outline-none focus:border-indigo-500 resize-none"
                />
              </div>

              {/* Modal Buttons */}
              <div className="pt-3 flex items-center justify-end gap-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-xs transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Saving...' : editingExpense ? 'Update Transaction' : 'Save Transaction'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
